import { Form, FormField, FormSubmit, FormSubmissionsWithFieldsResponse, FormTable } from "@/app/types/form";
import { apiSlice } from "@/features/api/ApiSlice";

export const FormBuilder = apiSlice.injectEndpoints({
  endpoints(builder) {
    return {
      getForms: builder.query<FormTable, { query: string }>({
        query: ({ query }) => ({
          url: `/form_builder?${query}`,
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }),
        providesTags: [{ type: "FormBuilder" }],
      }),
      getFormList: builder.query<{ value: number; label: string }[], void>({
        query: () => ({
          url: `/form_builder`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["FormBuilder"],
        transformResponse: (resp: { data: { id: number; name: string }[] }) => {
          return resp.data.map((form: { id: number; name: string }) => ({
            value: form.id,
            label: `${form.name}`,
          }));
        },
      }),
      getPublishedFormList: builder.query<{ value: number; label: string }[], void>({
        query: () => ({
          url: `/form_builder/published-forms`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["FormBuilder"],
        transformResponse: (resp: { data: { id: number; name: string }[] }) => {
          return resp.data.map((form: { id: number; name: string }) => ({
            value: form.id,
            label: `${form.name}`,
          }));
        },
      }),
      createForm: builder.mutation<Form, Partial<Form>>({
        query: (body) => ({
          url: "/form_builder",
          method: "POST",
          body,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }),
        invalidatesTags: [{ type: "FormBuilder" }],
      }),
      generateAI: builder.mutation<any, object>({
        query: (body) => ({
          url: "/form_builder/ai",
          method: "POST",
          body,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }),
        invalidatesTags: [{ type: "FormBuilder" }],
      }),
      updateForm: builder.mutation<Form, Partial<Form>>({
        query: (body) => ({
          url: `/form_builder/${body.id}`,
          method: "PUT",
          body,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }),
        invalidatesTags: [{ type: "FormBuilder" }],
      }),
      getFormFields: builder.query<string[], {}>({
        query: () => ({
          url: `/form_builder/form-fields`,
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }),
      }),
      getFormBySlug: builder.query<Form, string>({
        query: (slug) => ({
          url: `/form_builder/form-by-slug/${slug}`,
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }),
        providesTags: ["FormBuilder"],
      }),
      getFormById: builder.query<Form, number>({
        query: (id) => ({
          url: `/form_builder/form-by-id/${id}`,
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }),
        providesTags: ["FormBuilder"],
      }),
      getFormVersion: builder.query<Form, string>({
        query: (slug) => ({
          url: `/form_builder/form-version/${slug}`,
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }),
        providesTags: ["FormBuilder"],
      }),
      submitForm: builder.mutation({
        query: (body) => {
          const headers = { Accept: "application/json" };
          const url = `/form_builder/submit`;
          const payload = new FormData();

          for (const key in body) {
            if (key === "data" && typeof body[key] === "object") {
              payload.append(key, JSON.stringify(body[key]));
            } else if (key === "files" && Array.isArray(body[key])) {
              body[key].forEach((file) => {
                payload.append("files", file);
              });
            } else if (body[key] !== undefined && body[key] !== null) {
              payload.append(key, String(body[key]));
            }
          }

          return {
            url,
            method: "POST",
            body: payload,
            headers,
          };
        },
        invalidatesTags: ["FormBuilder"],
      }),
      getFormSubmission: builder.query<Form, any>({
        query: (id) => ({
          url: `/form_builder/submission/${id}`,
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }),
        providesTags: ["FormBuilder"],
      }),
      getAllFormSubmissions: builder.query<
        any,
        {
          form_id: string | number;
          offset?: number;
          limit?: number;
          search_key?: string;
          sort_key?: string;
          sort_order?: string;
          status?: string;
        }
      >({
        query: ({ form_id, ...params }) => {
          const queryParams = new URLSearchParams();
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              queryParams.append(key, String(value));
            }
          });
          const queryString = queryParams.toString();
          return {
            url: `/form_builder/form_responses/${form_id}${queryString ? `?${queryString}` : ""}`,
            method: "GET",
          };
        },
        providesTags: ["FormBuilder"],
      }),
      getFormSubmissionsWithFields: builder.query<
        FormSubmissionsWithFieldsResponse,
        {
          form_id: string | number;
          offset?: number;
          limit?: number;
          search_key?: string;
          sort_key?: string;
          sort_order?: string;
          status?: string;
        }
      >({
        query: ({ form_id, ...params }) => {
          const queryParams = new URLSearchParams();
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              queryParams.append(key, String(value));
            }
          });
          const queryString = queryParams.toString();
          return {
            url: `/form_builder/form_responses_with_fields/${form_id}${queryString ? `?${queryString}` : ""}`,
            method: "GET",
          };
        },
        providesTags: ["FormBuilder"],
      }),
      changeFormStatus: builder.mutation<Form, Partial<Form>>({
        query: (body) => ({
          url: `/form_builder/${body.id}/status?status=${body.status}`,
          method: "PATCH",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }),
        invalidatesTags: [{ type: "FormBuilder" }],
      }),
      duplicateForm: builder.mutation<Form, Partial<Form>>({
        query: (body) => ({
          url: `/form_builder/${body.id}/duplicate`,
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }),
        invalidatesTags: [{ type: "FormBuilder" }],
      }),
    };
  },
});

export const {
  useGetFormFieldsQuery,
  useGetFormsQuery,
  useGetFormListQuery,
  useGetPublishedFormListQuery,
  useCreateFormMutation,
  useUpdateFormMutation,
  useGetFormBySlugQuery,
  useGetFormByIdQuery,
  useLazyGetFormByIdQuery,
  useGetFormVersionQuery,
  useLazyGetFormVersionQuery,
  useLazyGetFormBySlugQuery,
  useSubmitFormMutation,
  useGetFormSubmissionQuery,
  useGetFormSubmissionsWithFieldsQuery,
  useLazyGetFormSubmissionQuery,
  useGetAllFormSubmissionsQuery,
  useChangeFormStatusMutation,
  useDuplicateFormMutation,
  useGenerateAIMutation,
} = FormBuilder;
